import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Divider, Message, Panel, SelectPicker, Stack, toaster } from 'rsuite';

import {
  useActiveProvider,
  useImportActuals,
  useProjects,
  useTaskOrders,
  useTasks,
} from '../hooks/useQueries';
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';
import usePermissions from '../hooks/usePermissions';

import './ImportHoursPage.css';

const isIsoDate = (s) => typeof s === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);

const normalizeMonthStartIso = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const direct = trimmed.slice(0, 10);
    if (isIsoDate(direct)) return `${direct.slice(0, 7)}-01`;

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}-01`;
    }
    return null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }

  if (value && typeof value === 'object') {
    if (typeof value.text === 'string') return normalizeMonthStartIso(value.text);
    if (value.result !== undefined) return normalizeMonthStartIso(value.result);
  }

  return null;
};

const toCellText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  if (value && typeof value === 'object') {
    if (typeof value.text === 'string') return value.text;
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => String(part?.text || '')).join('');
    }
    if (value.result !== undefined) return toCellText(value.result);
  }
  return String(value);
};

const toCellNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').trim();
    if (!cleaned) return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  if (value && typeof value === 'object') {
    if (value.result !== undefined) return toCellNumber(value.result);
    if (typeof value.text === 'string') return toCellNumber(value.text);
  }
  return null;
};

const formatMonthLabel = (monthStart) => {
  if (!isIsoDate(monthStart)) return monthStart;
  const y = Number(monthStart.slice(0, 4));
  const m = Number(monthStart.slice(5, 7));
  if (!Number.isInteger(y) || !Number.isInteger(m)) return monthStart;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n - Math.round(n)) < 0.000001) return String(Math.round(n));
  return n.toFixed(2);
};

const buildDefaultTemplateMonths = (count = 12) => {
  const safeCount = Math.max(1, Number.isInteger(Number(count)) ? Number(count) : 12);
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(end.getFullYear(), end.getMonth() - (safeCount - 1), 1);
  const out = [];
  for (let i = 0; i < safeCount; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    out.push(`${y}-${m}-01`);
  }
  return out;
};

const addMonthsIso = (monthStart, deltaMonths) => {
  const ms = normalizeMonthStartIso(monthStart);
  if (!ms) return null;

  const y = Number(ms.slice(0, 4));
  const m0 = Number(ms.slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return null;

  const d = new Date(y, m0 + Number(deltaMonths || 0), 1);
  return normalizeMonthStartIso(d);
};

const buildProjectDurationMonths = (project) => {
  const projectStartRaw = project?.startDate ?? project?.start_date ?? project?.start ?? null;
  const projectEndRaw = project?.endDate ?? project?.end_date ?? project?.end ?? null;

  let startMonth = normalizeMonthStartIso(projectStartRaw);
  const configuredEndMonth = normalizeMonthStartIso(projectEndRaw);
  const currentMonth = normalizeMonthStartIso(new Date());
  let endMonth = configuredEndMonth && currentMonth
    ? (configuredEndMonth < currentMonth ? configuredEndMonth : currentMonth)
    : currentMonth;

  if (startMonth && !endMonth) endMonth = startMonth;

  if (!startMonth && endMonth) startMonth = endMonth;
  if (!startMonth || !endMonth) return buildDefaultTemplateMonths(12);

  if (startMonth > endMonth) {
    const swap = startMonth;
    startMonth = endMonth;
    endMonth = swap;
  }

  const months = [];
  const maxMonths = 240;
  let cursor = startMonth;
  let guard = 0;
  while (cursor && cursor <= endMonth && guard < maxMonths) {
    months.push(cursor);
    cursor = addMonthsIso(cursor, 1);
    guard += 1;
  }

  return months.length > 0 ? months : buildDefaultTemplateMonths(12);
};

const resolveApiBaseUrl = () => {
  const raw = String(API_CONFIG?.BASE_URL || '').trim();
  if (!raw) return '/api';

  const withoutTrailing = raw.replace(/\/+$/, '');
  if (/\/api$/i.test(withoutTrailing)) return withoutTrailing;
  return `${withoutTrailing}/api`;
};

const sanitizeFileNamePart = (value) =>
  String(value || 'Project')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }
};

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 KB';

  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function ImportHoursPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const routeProjectId = Number(params?.projectId);
  const queryProjectId = Number(searchParams.get('projectId'));
  const queryTaskOrderId = Number(searchParams.get('taskOrderId'));

  const initialProjectId = Number.isInteger(routeProjectId)
    ? routeProjectId
    : (Number.isInteger(queryProjectId) ? queryProjectId : null);
  const initialTaskOrderId = Number.isInteger(queryTaskOrderId) ? queryTaskOrderId : null;

  const permissions = usePermissions();
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId);
  const [selectedTaskOrderId, setSelectedTaskOrderId] = useState(initialTaskOrderId);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewMonths, setPreviewMonths] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [importStatus, setImportStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const authenticatedFetch = useAuthenticatedFetch();

  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: taskOrders = [], isLoading: taskOrdersLoading } = useTaskOrders(
    Number.isInteger(selectedProjectId) ? selectedProjectId : null,
    { enabled: Number.isInteger(selectedProjectId) }
  );

  const { data: tasks = [], isLoading: tasksLoading } = useTasks(
    {
      projectId: Number.isInteger(selectedProjectId) ? selectedProjectId : null,
      taskOrderId: Number.isInteger(selectedTaskOrderId) ? selectedTaskOrderId : null,
    },
    { enabled: Number.isInteger(selectedProjectId) }
  );

  const { data: activeProvider } = useActiveProvider();
  const isManualActive = activeProvider?.name === 'manual';

  const importActuals = useImportActuals();

  // Importing actual hours needs Manage Finance, so only those projects are offered.
  const projectOptions = useMemo(
    () => (Array.isArray(projects) ? projects : [])
      .filter((p) => Array.isArray(p?.access) && p.access.includes('finance'))
      .map((p) => {
        const id = Number(p?.id);
        if (!Number.isInteger(id)) return null;
        return { value: id, label: String(p?.name || `Project ${id}`) };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [projects]
  );

  const selectedProject = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    const pid = Number(selectedProjectId);
    if (!Number.isInteger(pid)) return null;
    return list.find((p) => Number(p?.id) === pid) || null;
  }, [projects, selectedProjectId]);

  const taskOrderOptions = useMemo(
    () => (Array.isArray(taskOrders) ? taskOrders : [])
      .map((to) => {
        const id = Number(to?.id);
        if (!Number.isInteger(id)) return null;
        const number = to?.taskOrderNumber ? ` (${to.taskOrderNumber})` : '';
        return { value: id, label: `${String(to?.title || `Task Order ${id}`)}${number}` };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [taskOrders]
  );

  useEffect(() => {
    if (!Number.isInteger(selectedProjectId)) return;
    if (taskOrderOptions.length === 1 && !Number.isInteger(selectedTaskOrderId)) {
      setSelectedTaskOrderId(Number(taskOrderOptions[0].value));
    }
  }, [selectedProjectId, selectedTaskOrderId, taskOrderOptions]);

  const templateRows = useMemo(() => {
    const sourceTasks = Array.isArray(tasks) ? tasks : [];
    const rows = [];

    for (const task of sourceTasks) {
      const taskId = Number(task?.id);
      if (!Number.isInteger(taskId)) continue;

      const activities = Array.isArray(task?.activities) ? task.activities : [];
      for (const activity of activities) {
        const activityId = Number(activity?.id);
        if (!Number.isInteger(activityId)) continue;

        const disciplineName = String(activity?.name || `Activity ${activityId}`).trim();
        const rosterByUserId = new Map();

        const leadUserId = Number(activity?.leadUserId);
        const leadName = String(activity?.leadName || '').trim();
        if (Number.isInteger(leadUserId)) {
          rosterByUserId.set(leadUserId, leadName || `User ${leadUserId}`);
        }

        const supporters = Array.isArray(activity?.supporters) ? activity.supporters : [];
        for (const supporter of supporters) {
          const supportUserId = Number(supporter?.userId);
          if (!Number.isInteger(supportUserId)) continue;
          const supportName = String(supporter?.name || '').trim();
          if (!rosterByUserId.has(supportUserId)) {
            rosterByUserId.set(supportUserId, supportName || `User ${supportUserId}`);
          }
        }

        for (const [userId, userName] of rosterByUserId.entries()) {
          rows.push({
            disciplineName,
            taskId,
            activityId,
            userId,
            userName,
          });
        }
      }
    }

    return rows.sort((a, b) => {
      const disciplineCmp = String(a.disciplineName).localeCompare(String(b.disciplineName));
      if (disciplineCmp !== 0) return disciplineCmp;
      return String(a.userName).localeCompare(String(b.userName));
    });
  }, [tasks]);

  const handleDownloadTemplate = async () => {
    try {
      if (!Number.isInteger(selectedProjectId)) {
        setErrorMessage('Select a project before downloading a template.');
        return;
      }

      if (templateRows.length === 0) {
        setErrorMessage('No disciplines with assigned lead/support users were found for this selection.');
        return;
      }

      setErrorMessage('');
      const monthHeaders = buildProjectDurationMonths(selectedProject);
      const monthSet = new Set(monthHeaders);

      const templateRowScope = new Set(
        templateRows.map((row) => `${Number(row.taskId)}|${Number(row.activityId)}|${Number(row.userId)}`)
      );

      const qs = new URLSearchParams();
      qs.set('projectId', String(selectedProjectId));
      if (Number.isInteger(selectedTaskOrderId)) qs.set('taskOrderId', String(selectedTaskOrderId));
      qs.set('months', monthHeaders.join(','));

      const prefillResponse = await authenticatedFetch(`${resolveApiBaseUrl()}/actuals/import/prefill?${qs.toString()}`);
      if (!prefillResponse.ok) {
        let message = 'Failed to load existing actuals for template prefill.';
        try {
          const payload = await prefillResponse.json();
          if (payload?.error) message = String(payload.error);
        } catch {
          // Keep default message.
        }
        throw new Error(message);
      }

      const prefillPayload = await prefillResponse.json();
      const prefillRows = Array.isArray(prefillPayload?.rows) ? prefillPayload.rows : [];

      const prefilledHoursByKey = new Map();
      for (const row of prefillRows) {
        const taskId = Number(row?.taskId);
        const activityId = Number(row?.activityId);
        const userId = Number(row?.userId);
        const monthStart = normalizeMonthStartIso(row?.monthStart);
        const hours = Number(row?.hours);

        if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !Number.isInteger(userId)) continue;
        if (!monthStart || !monthSet.has(monthStart)) continue;
        if (!Number.isFinite(hours) || hours <= 0) continue;

        const rowScopeKey = `${taskId}|${activityId}|${userId}`;
        if (!templateRowScope.has(rowScopeKey)) continue;

        const key = `${rowScopeKey}|${monthStart}`;
        prefilledHoursByKey.set(key, Number(prefilledHoursByKey.get(key) ?? 0) + hours);
      }

      const mod = await import('exceljs/dist/exceljs.min.js');
      const ExcelJS = mod?.default || mod;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Import Hours');

      const header = ['taskId', 'activityId', 'userId', 'Discipline', 'Employee', ...monthHeaders];
      worksheet.addRow(header);

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

      const legendRow = worksheet.addRow(['', '', '', 'Legend', 'Blue cells = previously imported hours']);
      legendRow.getCell(4).font = { bold: true, color: { argb: 'FF0B4A8B' } };
      legendRow.getCell(5).font = { bold: true, color: { argb: 'FF0B4A8B' } };
      legendRow.getCell(5).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDDEBFF' },
      };

      worksheet.views = [{ state: 'frozen', xSplit: 5, ySplit: 2 }];

      worksheet.getColumn(1).hidden = true;
      worksheet.getColumn(2).hidden = true;
      worksheet.getColumn(3).hidden = true;
      worksheet.getColumn(4).width = 34;
      worksheet.getColumn(5).width = 28;
      for (let col = 6; col < 6 + monthHeaders.length; col += 1) {
        worksheet.getColumn(col).width = 14;
      }
      const firstVisibleTemplateCol = 4;
      const lastVisibleTemplateCol = 5 + monthHeaders.length;

      const grouped = new Map();
      for (const row of templateRows) {
        const key = String(row.disciplineName || 'Discipline');
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(row);
      }

      for (const [disciplineName, disciplineRows] of grouped.entries()) {
        const separator = worksheet.addRow(['', '', '', disciplineName, '']);
        separator.font = { bold: true };
        for (let col = firstVisibleTemplateCol; col <= lastVisibleTemplateCol; col += 1) {
          const cell = separator.getCell(col);
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF3F4F6' },
          };
          cell.border = {
            ...(cell.border || {}),
            top: { style: 'thin', color: { argb: 'FF9AA4B2' } },
            bottom: { style: 'thin', color: { argb: 'FF9AA4B2' } },
          };
        }

        for (const row of disciplineRows) {
          const values = [
            row.taskId,
            row.activityId,
            row.userId,
            '',
            row.userName,
            ...monthHeaders.map((monthStart) => {
              const key = `${Number(row.taskId)}|${Number(row.activityId)}|${Number(row.userId)}|${monthStart}`;
              const prefilled = Number(prefilledHoursByKey.get(key));
              return Number.isFinite(prefilled) && prefilled > 0 ? prefilled : '';
            }),
          ];

          const dataRow = worksheet.addRow(values);

          for (let idx = 0; idx < monthHeaders.length; idx += 1) {
            const monthStart = monthHeaders[idx];
            const key = `${Number(row.taskId)}|${Number(row.activityId)}|${Number(row.userId)}|${monthStart}`;
            const prefilled = Number(prefilledHoursByKey.get(key));
            if (!Number.isFinite(prefilled) || prefilled <= 0) continue;

            const cell = dataRow.getCell(6 + idx);
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFDDEBFF' },
            };
            cell.font = { color: { argb: 'FF0B4A8B' }, bold: true };
          }
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const projectName = projectOptions.find((p) => Number(p.value) === Number(selectedProjectId))?.label || 'Project';
      const fileName = `${sanitizeFileNamePart(projectName)} - Hours Import Template.xlsx`;
      downloadBlob(blob, fileName);

      toaster.push(
        <Message showIcon type="success" closable>
          Template downloaded ({monthHeaders.length} month{monthHeaders.length === 1 ? '' : 's'} through current month).
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to generate template.');
    }
  };

  const handleParseFile = async () => {
    try {
      if (!uploadedFile) {
        setErrorMessage('Choose an .xlsx file first.');
        return;
      }

      setImportStatus('parsing');
      setErrorMessage('');
      setWarnings([]);

      const buffer = await uploadedFile.arrayBuffer();
      const mod = await import('exceljs/dist/exceljs.min.js');
      const ExcelJS = mod?.default || mod;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const worksheet = workbook.getWorksheet('Import Hours') || workbook.worksheets?.[0];
      if (!worksheet) {
        throw new Error('No worksheet found in uploaded workbook.');
      }

      const headerRow = worksheet.getRow(1);
      const monthColumns = [];
      for (let col = 6; col <= worksheet.columnCount; col += 1) {
        const monthStart = normalizeMonthStartIso(toCellText(headerRow.getCell(col).value));
        if (monthStart) {
          monthColumns.push({ col, monthStart });
        }
      }

      if (monthColumns.length === 0) {
        throw new Error('No month columns were found. Ensure month headers are ISO dates (YYYY-MM-01).');
      }

      const nextWarnings = [];
      const nextRows = [];
      let currentDisciplineName = '';

      for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber += 1) {
        const row = worksheet.getRow(rowNumber);

        const taskId = Number(toCellText(row.getCell(1).value));
        const activityId = Number(toCellText(row.getCell(2).value));
        const userId = Number(toCellText(row.getCell(3).value));
        const disciplineName = String(toCellText(row.getCell(4).value) || '').trim();
        const userName = String(toCellText(row.getCell(5).value) || '').trim();

        if (disciplineName) currentDisciplineName = disciplineName;

        if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !Number.isInteger(userId)) {
          continue;
        }

        for (const month of monthColumns) {
          const cellValue = row.getCell(month.col).value;
          const hours = toCellNumber(cellValue);
          if (hours === null) {
            const raw = toCellText(cellValue).trim();
            if (raw) {
              nextWarnings.push(`Invalid numeric value at row ${rowNumber}, ${month.monthStart}: ${raw}`);
            }
            continue;
          }

          if (hours <= 0) continue;

          if (hours > 744) {
            nextWarnings.push(`Hours exceed monthly limit at row ${rowNumber}, ${month.monthStart}: ${hours}`);
          }

          nextRows.push({
            disciplineName: disciplineName || currentDisciplineName || `Activity ${activityId}`,
            taskId,
            activityId,
            userId,
            userName: userName || `User ${userId}`,
            monthStart: month.monthStart,
            hours,
          });
        }
      }

      if (nextRows.length === 0) {
        throw new Error('No importable rows were found in the workbook.');
      }

      setPreviewRows(nextRows);
      setPreviewMonths(monthColumns.map((m) => m.monthStart));
      setWarnings(Array.from(new Set(nextWarnings)));
      setImportStatus('idle');
    } catch (err) {
      setImportStatus('error');
      setErrorMessage(err?.message || 'Failed to parse workbook.');
      setPreviewRows([]);
      setPreviewMonths([]);
    }
  };

  const previewSummary = useMemo(() => {
    const months = previewMonths.length > 0
      ? [...previewMonths]
      : Array.from(new Set(previewRows.map((r) => String(r.monthStart || '')))).filter(Boolean).sort();

    const rowMap = new Map();
    for (const row of previewRows) {
      const key = `${row.taskId}|${row.activityId}|${row.userId}|${row.disciplineName}`;
      if (!rowMap.has(key)) {
        rowMap.set(key, {
          taskId: Number(row.taskId),
          activityId: Number(row.activityId),
          userId: Number(row.userId),
          disciplineName: String(row.disciplineName || ''),
          userName: String(row.userName || ''),
          hoursByMonth: Object.fromEntries(months.map((m) => [m, 0])),
          warning: false,
        });
      }

      const target = rowMap.get(key);
      const month = String(row.monthStart || '');
      const hours = Number(row.hours ?? 0);
      target.hoursByMonth[month] = Number(target.hoursByMonth[month] ?? 0) + hours;
      if (hours > 744) target.warning = true;
    }

    const flatRows = Array.from(rowMap.values()).sort((a, b) => {
      const dcmp = String(a.disciplineName).localeCompare(String(b.disciplineName));
      if (dcmp !== 0) return dcmp;
      return String(a.userName).localeCompare(String(b.userName));
    });

    const groups = new Map();
    for (const row of flatRows) {
      const key = String(row.disciplineName || 'Discipline');
      if (!groups.has(key)) {
        groups.set(key, {
          disciplineName: key,
          rows: [],
          totalsByMonth: Object.fromEntries(months.map((m) => [m, 0])),
        });
      }
      const group = groups.get(key);
      group.rows.push(row);
      for (const month of months) {
        group.totalsByMonth[month] = Number(group.totalsByMonth[month] ?? 0) + Number(row.hoursByMonth[month] ?? 0);
      }
    }

    const grandTotalsByMonth = Object.fromEntries(months.map((m) => [m, 0]));
    for (const group of groups.values()) {
      for (const month of months) {
        grandTotalsByMonth[month] = Number(grandTotalsByMonth[month] ?? 0) + Number(group.totalsByMonth[month] ?? 0);
      }
    }

    return {
      months,
      groups: Array.from(groups.values()),
      grandTotalsByMonth,
    };
  }, [previewMonths, previewRows]);

  const handleSelectedFile = (file) => {
    if (!file) return;

    const fileName = String(file?.name || '').trim();
    if (!/\.xlsx$/i.test(fileName)) {
      setErrorMessage('Please choose an .xlsx template file.');
      return;
    }

    setUploadedFile(file);
    setPreviewRows([]);
    setPreviewMonths([]);
    setWarnings([]);
    setImportStatus('idle');
    setErrorMessage('');
  };

  const openFilePicker = () => {
    if (!isManualActive) return;
    fileInputRef.current?.click?.();
  };

  const finishImport = async () => {
    try {
      if (previewRows.length === 0) {
        setErrorMessage('Parse a completed template before importing.');
        return;
      }

      if (!Number.isInteger(selectedProjectId)) {
        setErrorMessage('Select a project before importing.');
        return;
      }

      setImportStatus('importing');
      setErrorMessage('');

      const entries = previewRows.map((row) => ({
        taskId: Number(row.taskId),
        activityId: Number(row.activityId),
        userId: Number(row.userId),
        monthStart: String(row.monthStart).slice(0, 10),
        hours: Number(row.hours),
      }));

      const result = await importActuals.mutateAsync({
        entries,
        projectId: selectedProjectId,
        taskOrderId: Number.isInteger(selectedTaskOrderId) ? selectedTaskOrderId : null,
      });

      const rejectedCount = Number(result?.rejected ?? 0);
      if (rejectedCount > 0) {
        const rejectedDetails = Array.isArray(result?.rejectedEntries)
          ? result.rejectedEntries
              .slice(0, 3)
              .map((r) => String(r?.reason || 'out_of_scope'))
              .join(', ')
          : '';

        toaster.push(
          <Message showIcon type="warning" closable>
            Skipped {rejectedCount} out-of-scope group{rejectedCount === 1 ? '' : 's'}.
            {rejectedDetails ? ` Reasons: ${rejectedDetails}.` : ''}
          </Message>,
          { placement: 'topEnd', duration: 4500 }
        );
      }

      setImportStatus('done');
      toaster.push(
        <Message showIcon type="success" closable>
          Import complete. Imported {Number(result?.imported ?? 0)} rows{Number(result?.skipped ?? 0) > 0 ? `, skipped ${Number(result?.skipped ?? 0)}` : ''}.
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );

      if (Number.isInteger(selectedProjectId) && Number.isInteger(selectedTaskOrderId)) {
        navigate(`/projects/${selectedProjectId}/task-orders/${selectedTaskOrderId}/actuals`);
      } else if (Number.isInteger(selectedProjectId)) {
        navigate(`/projects/${selectedProjectId}/actuals`);
      }
    } catch (err) {
      setImportStatus('error');
      setErrorMessage(err?.message || 'Import failed.');
    }
  };

  return (
    <div style={{ width: '92%', maxWidth: 1600, margin: '0 auto' }}>
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>Import Hours</h3>
        <Button appearance="ghost" onClick={() => navigate(-1)}>Back</Button>
      </Stack>

      <Divider />

      {!isManualActive ? (
        <Message type="warning" showIcon style={{ marginBottom: 12 }}>
          The active hours provider is not manual. Bulk import is only available when the manual provider is active.
        </Message>
      ) : null}

      {errorMessage ? (
        <Message type="error" showIcon style={{ marginBottom: 12 }}>
          {errorMessage}
        </Message>
      ) : null}

      <Panel bordered header="Step 1 - Select Project" style={{ marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Project</div>
            <SelectPicker
              block
              searchable
              cleanable={false}
              placeholder={projectsLoading ? 'Loading projects...' : 'Select project'}
              data={projectOptions}
              value={Number.isInteger(selectedProjectId) ? selectedProjectId : null}
              onChange={(value) => {
                const nextProjectId = Number(value);
                setSelectedProjectId(Number.isInteger(nextProjectId) ? nextProjectId : null);
                setSelectedTaskOrderId(null);
                setPreviewRows([]);
                setPreviewMonths([]);
                setWarnings([]);
              }}
            />
          </div>

          {taskOrderOptions.length > 1 ? (
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Task Order</div>
              <SelectPicker
                block
                searchable
                cleanable
                placeholder={taskOrdersLoading ? 'Loading task orders...' : 'Select task order (optional)'}
                data={taskOrderOptions}
                value={Number.isInteger(selectedTaskOrderId) ? selectedTaskOrderId : null}
                onChange={(value) => {
                  const nextTaskOrderId = Number(value);
                  setSelectedTaskOrderId(Number.isInteger(nextTaskOrderId) ? nextTaskOrderId : null);
                  setPreviewRows([]);
                  setPreviewMonths([]);
                  setWarnings([]);
                }}
              />
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 12 }}>
          <Button
            appearance="primary"
            onClick={handleDownloadTemplate}
            disabled={!isManualActive || !Number.isInteger(selectedProjectId) || tasksLoading}
          >
            Download Template
          </Button>
        </div>
      </Panel>

      <Panel bordered header="Step 2 - Upload Completed Template" style={{ marginBottom: 12 }}>
        <div className="ppc-import-upload-card">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target?.files?.[0] || null;
              handleSelectedFile(file);
              event.target.value = '';
            }}
          />

          <div
            className={`ppc-import-dropzone${isDragOver ? ' ppc-import-dropzone--active' : ''}${!isManualActive ? ' ppc-import-dropzone--disabled' : ''}`}
            role="button"
            tabIndex={isManualActive ? 0 : -1}
            aria-disabled={!isManualActive}
            onClick={openFilePicker}
            onKeyDown={(event) => {
              if (!isManualActive) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openFilePicker();
              }
            }}
            onDragEnter={(event) => {
              if (!isManualActive) return;
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragOver={(event) => {
              if (!isManualActive) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
              setIsDragOver(true);
            }}
            onDragLeave={(event) => {
              if (!isManualActive) return;
              event.preventDefault();
              setIsDragOver(false);
            }}
            onDrop={(event) => {
              if (!isManualActive) return;
              event.preventDefault();
              setIsDragOver(false);
              const droppedFile = event.dataTransfer?.files?.[0] || null;
              handleSelectedFile(droppedFile);
            }}
          >
            <div className="ppc-import-dropzone__title">
              {uploadedFile ? 'Template loaded' : 'Drop your completed .xlsx template here'}
            </div>
            <div className="ppc-import-dropzone__subtitle">
              {uploadedFile ? 'You can drop a different file to replace it, or click to browse.' : 'Drag and drop or click to browse files.'}
            </div>

            {uploadedFile ? (
              <div className="ppc-import-dropzone__file-pill">
                <span className="ppc-import-dropzone__file-name">{uploadedFile.name}</span>
                <span className="ppc-import-dropzone__file-size">{formatFileSize(uploadedFile.size)}</span>
              </div>
            ) : null}
          </div>

          <Stack spacing={8} wrap>
            <Button appearance="subtle" onClick={openFilePicker} disabled={!isManualActive}>
              Choose File
            </Button>
            <Button
              appearance="primary"
              onClick={handleParseFile}
              loading={importStatus === 'parsing'}
              disabled={!isManualActive || !uploadedFile}
            >
              Parse and Preview
            </Button>

            {uploadedFile ? (
              <Button
                appearance="ghost"
                onClick={() => {
                  setUploadedFile(null);
                  setPreviewRows([]);
                  setPreviewMonths([]);
                  setWarnings([]);
                  setImportStatus('idle');
                }}
              >
                Remove File
              </Button>
            ) : null}
          </Stack>
        </div>
      </Panel>

      <Panel bordered header="Step 3 - Preview and Confirm">
        {warnings.length > 0 ? (
          <Message type="warning" showIcon style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div>{warnings.length} warning(s) found while parsing.</div>
              <div style={{ fontSize: 12, color: '#666' }}>{warnings.slice(0, 5).join(' | ')}</div>
            </div>
          </Message>
        ) : null}

        {previewRows.length === 0 ? (
          <Message type="info" showIcon>
            Upload and parse a completed template to preview rows before importing.
          </Message>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px 6px' }}>Discipline</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px 6px' }}>Employee</th>
                  {previewSummary.months.map((month) => (
                    <th key={month} style={{ textAlign: 'right', borderBottom: '1px solid #ddd', padding: '8px 6px' }}>
                      {formatMonthLabel(month)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewSummary.groups.map((group) => (
                  <Fragment key={`group|${group.disciplineName}`}>
                    {group.rows.map((row) => (
                      <tr key={`${row.taskId}|${row.activityId}|${row.userId}`} style={row.warning ? { background: '#fff9e6' } : undefined}>
                        <td style={{ padding: '6px', borderBottom: '1px solid #f0f0f0' }}>{row.disciplineName}</td>
                        <td style={{ padding: '6px', borderBottom: '1px solid #f0f0f0' }}>
                          {row.userName}
                          {row.warning ? <span style={{ marginLeft: 8, color: '#ad6800', fontSize: 12 }}>Warning</span> : null}
                        </td>
                        {previewSummary.months.map((month) => (
                          <td key={`${row.taskId}|${row.activityId}|${row.userId}|${month}`} style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #f0f0f0' }}>
                            {formatHours(row.hoursByMonth[month])}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr style={{ background: '#f8fafb' }}>
                      <td style={{ padding: '7px 6px', fontWeight: 700 }} colSpan={2}>{group.disciplineName} Total</td>
                      {previewSummary.months.map((month) => (
                        <td key={`group-total|${group.disciplineName}|${month}`} style={{ textAlign: 'right', padding: '7px 6px', fontWeight: 700 }}>
                          {formatHours(group.totalsByMonth[month])}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                ))}
                <tr style={{ background: '#eef5ff' }}>
                  <td style={{ padding: '8px 6px', fontWeight: 800 }} colSpan={2}>Grand Total</td>
                  {previewSummary.months.map((month) => (
                    <td key={`grand-total|${month}`} style={{ textAlign: 'right', padding: '8px 6px', fontWeight: 800 }}>
                      {formatHours(previewSummary.grandTotalsByMonth[month])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <Stack spacing={10} style={{ marginTop: 12 }}>
          {permissions.canProject(selectedProjectId, 'finance') ? (
            <Button
              appearance="primary"
              onClick={() => void finishImport()}
              loading={importStatus === 'importing' || importActuals.isPending}
              disabled={!isManualActive || previewRows.length === 0}
            >
              Finish Import
            </Button>
          ) : null}
          {importStatus === 'done' ? (
            <Button appearance="ghost" onClick={() => navigate(-1)}>
              Back to Previous Page
            </Button>
          ) : null}
        </Stack>
      </Panel>
    </div>
  );
}
